import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import {
  Search,
  ZoomIn,
  ZoomOut,
  Filter,
  Plus,
  X,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// ---------- date helpers (real math, no hardcoded pixel positions) ----------
const DAY_MS = 1000 * 60 * 60 * 24;
const addDays = (date, n) => new Date(date.getTime() + n * DAY_MS);
const diffDays = (a, b) => Math.round((a.getTime() - b.getTime()) / DAY_MS);
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const fmt = (d) =>
  `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`;
const toInputDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fromInputDate = (s) => {
  const [y, m, day] = s.split("-").map(Number);
  return new Date(y, m - 1, day);
};

const TODAY = new Date(2026, 4, 18);
const ROW_HEIGHT = 52;
const TABLE_WIDTH = 460;
const BASE_PX_PER_DAY = { Year: 3.4, Month: 40 };

const initialTasks = [
  {
    id: 1,
    name: "Project Initiation",
    start: new Date(2026, 0, 15),
    end: new Date(2026, 0, 20),
    progress: 100,
    color: "#22c55e",
  },
  {
    id: 2,
    name: "Site Survey",
    start: new Date(2026, 0, 21),
    end: new Date(2026, 0, 28),
    progress: 100,
    color: "#0ea5e9",
  },
  {
    id: 3,
    name: "Concept Design",
    start: new Date(2026, 0, 29),
    end: new Date(2026, 1, 15),
    progress: 100,
    color: "#22c55e",
  },
  {
    id: 4,
    name: "Layout Design",
    start: new Date(2026, 1, 16),
    end: new Date(2026, 2, 5),
    progress: 100,
    color: "#3b82f6",
  },
  {
    id: 5,
    name: "3D Design",
    start: new Date(2026, 2, 6),
    end: new Date(2026, 2, 25),
    progress: 60,
    color: "#4338ca",
  },
  {
    id: 6,
    name: "Material Selection",
    start: new Date(2026, 2, 26),
    end: new Date(2026, 3, 10),
    progress: 40,
    color: "#a855f7",
  },
  {
    id: 7,
    name: "Working Drawings",
    start: new Date(2026, 3, 11),
    end: new Date(2026, 5, 15),
    progress: 30,
    color: "#8b5cf6",
  },
  {
    id: 8,
    name: "BOQ & Estimates",
    start: new Date(2026, 5, 16),
    end: new Date(2026, 5, 30),
    progress: 20,
    color: "#fb923c",
  },
  {
    id: 9,
    name: "Client Approval",
    start: new Date(2026, 6, 1),
    end: new Date(2026, 6, 10),
    progress: 10,
    color: "#f97316",
  },
  {
    id: 10,
    name: "Execution",
    start: new Date(2026, 6, 11),
    end: new Date(2026, 8, 20),
    progress: 0,
    color: "#94a3b8",
  },
  {
    id: 11,
    name: "Handover",
    start: new Date(2026, 8, 21),
    end: new Date(2026, 8, 30),
    progress: 0,
    color: "#64748b",
  },
];

// ---------- period math: everything derives from viewType + anchor date ----------
function getPeriodRange(viewType, anchor) {
  if (viewType === "Year") {
    return {
      start: new Date(anchor.getFullYear(), 0, 1),
      end: new Date(anchor.getFullYear() + 1, 0, 1),
    };
  }
  // Month
  return {
    start: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
    end: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1),
  };
}

function shiftAnchor(viewType, anchor, dir) {
  if (viewType === "Year") return new Date(anchor.getFullYear() + dir, 0, 1);
  return new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
}

function periodLabel(viewType, start) {
  if (viewType === "Year") return String(start.getFullYear());
  return start.toLocaleString("en-US", { month: "long", year: "numeric" });
}

// Year view -> one column per month. Month view -> one column per day.
function buildColumns(viewType, start, end, pxPerDay) {
  const cols = [];
  if (viewType === "Year") {
    let cur = new Date(start);
    while (cur < end) {
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const days = diffDays(next, cur);
      cols.push({
        key: `${cur.getFullYear()}-${cur.getMonth()}`,
        label: cur.toLocaleString("en-US", { month: "short" }),
        left: diffDays(cur, start) * pxPerDay,
        width: days * pxPerDay,
      });
      cur = next;
    }
  } else {
    let cur = new Date(start);
    while (cur < end) {
      cols.push({
        key: toInputDate(cur),
        label: String(cur.getDate()),
        sub: cur.toLocaleString("en-US", { weekday: "narrow" }),
        left: diffDays(cur, start) * pxPerDay,
        width: pxPerDay,
        weekend: cur.getDay() === 0 || cur.getDay() === 6,
      });
      cur = addDays(cur, 1);
    }
  }
  return cols;
}

export default function GanttChart() {
  const [tasks, setTasks] = useState(initialTasks);
  const [viewType, setViewType] = useState("Month");
  const [anchor, setAnchor] = useState(new Date(2026, 4, 1));
  const [zoomMult, setZoomMult] = useState(1);
  const [query, setQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [minProgress, setMinProgress] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // {id, field}
  const [highlightId, setHighlightId] = useState(null);
  const scrollRef = useRef(null);
  const drag = useRef(null);
  const rowRefs = useRef({});

  const { start: periodStart, end: periodEnd } = useMemo(
    () => getPeriodRange(viewType, anchor),
    [viewType, anchor],
  );

  const pxPerDay = BASE_PX_PER_DAY[viewType] * zoomMult;
  const totalWidth = diffDays(periodEnd, periodStart) * pxPerDay;
  const columns = useMemo(
    () => buildColumns(viewType, periodStart, periodEnd, pxPerDay),
    [viewType, periodStart, periodEnd, pxPerDay],
  );
  const label = periodLabel(viewType, periodStart);
  const todayInPeriod = TODAY >= periodStart && TODAY < periodEnd;
  const todayLeft = diffDays(TODAY, periodStart) * pxPerDay;

  // Only tasks overlapping the currently selected period are shown — that's the point of the picker
  const inPeriod = tasks.filter(
    (t) => t.end > periodStart && t.start < periodEnd,
  );
  const filtered = inPeriod.filter(
    (t) =>
      t.name.toLowerCase().includes(query.toLowerCase()) &&
      t.progress >= minProgress,
  );
  const totalHeight = filtered.length * ROW_HEIGHT;

  const goPrev = () => setAnchor((a) => shiftAnchor(viewType, a, -1));
  const goNext = () => setAnchor((a) => shiftAnchor(viewType, a, 1));
  const goToToday = () => {
    setAnchor(
      viewType === "Year"
        ? new Date(TODAY.getFullYear(), 0, 1)
        : new Date(TODAY.getFullYear(), TODAY.getMonth(), 1),
    );
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  };

  // switching view type: snap anchor to the matching period containing current anchor
  const onChangeViewType = (nextType) => {
    if (nextType === viewType) return;
    setViewType(nextType);
    if (nextType === "Year") setAnchor(new Date(anchor.getFullYear(), 0, 1));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  };

  const updateTask = (id, patch) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const addTask = () => {
    const sorted = [...inPeriod].sort((a, b) => a.end - b.end);
    let newStart = sorted.length
      ? addDays(sorted[sorted.length - 1].end, 1)
      : periodStart;
    if (newStart >= periodEnd) newStart = periodStart;
    let newEnd = addDays(newStart, 3);
    if (newEnd > periodEnd) newEnd = periodEnd;
    const id = Math.max(0, ...tasks.map((t) => t.id)) + 1;
    setTasks((prev) => [
      ...prev,
      {
        id,
        name: `New Task ${id}`,
        start: newStart,
        end: newEnd,
        progress: 0,
        color: "#0ea5e9",
      },
    ]);
    setQuery("");
    setMinProgress(0);
    setHighlightId(id);
  };

  useEffect(() => {
    if (highlightId == null) return;
    rowRefs.current[highlightId]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    const timer = setTimeout(() => setHighlightId(null), 2200);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const deleteTask = (id) =>
    setTasks((prev) => prev.filter((t) => t.id !== id));

  const exportCSV = () => {
    const rows = [
      ["Task Name", "Start Date", "End Date", "Progress"],
      ...inPeriod.map((t) => [
        t.name,
        toInputDate(t.start),
        toInputDate(t.end),
        `${t.progress}%`,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gantt-${label.replace(/\s/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- drag / resize (real: mutates task start/end in state) ----------
  const onBarMouseDown = (e, task, mode) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = {
      id: task.id,
      mode,
      startX: e.clientX,
      origStart: task.start,
      origEnd: task.end,
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = useCallback(
    (e) => {
      if (!drag.current) return;
      const { id, mode, startX, origStart, origEnd } = drag.current;
      const deltaDays = Math.round((e.clientX - startX) / pxPerDay);
      if (deltaDays === 0) return;
      setTasks((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;
          if (mode === "move")
            return {
              ...t,
              start: addDays(origStart, deltaDays),
              end: addDays(origEnd, deltaDays),
            };
          if (mode === "resize-left") {
            const s = addDays(origStart, deltaDays);
            return s < t.end ? { ...t, start: s } : t;
          }
          if (mode === "resize-right") {
            const en = addDays(origEnd, deltaDays);
            return en > t.start ? { ...t, end: en } : t;
          }
          return t;
        }),
      );
    },
    [pxPerDay],
  );

  const onMouseUp = useCallback(() => {
    drag.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  return (
    <div
      style={{
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        overflow: "hidden",
        color: "#1e293b",
        fontSize: 13,
        userSelect: drag.current ? "none" : "auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1px solid #eef0f3",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
            Gantt Chart
          </div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>
            Project: Magnira Office Interior
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: "#64748b", fontSize: 13 }}>View</span>
          <select
            value={viewType}
            onChange={(e) => onChangeViewType(e.target.value)}
            style={selectStyle}
          >
            <option>Month</option>
            <option>Year</option>
          </select>

          <button style={btnStyle} onClick={goToToday}>
            Today
          </button>

          <div style={{ position: "relative" }}>
            <button
              style={iconBtnStyle}
              onClick={() => setShowSearch((s) => !s)}
              title="Search tasks"
            >
              <Search size={15} color="#64748b" />
            </button>
            {showSearch && (
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search task..."
                style={{
                  position: "absolute",
                  top: "110%",
                  right: 0,
                  zIndex: 20,
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "6px 10px",
                  fontSize: 13,
                  width: 180,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              />
            )}
          </div>

          <button
            style={iconBtnStyle}
            onClick={() => setZoomMult((z) => Math.min(z + 0.4, 3))}
            title="Zoom in"
          >
            <ZoomIn size={15} color="#64748b" />
          </button>
          <button
            style={iconBtnStyle}
            onClick={() => setZoomMult((z) => Math.max(z - 0.4, 0.5))}
            title="Zoom out"
          >
            <ZoomOut size={15} color="#64748b" />
          </button>

          <div style={{ position: "relative" }}>
            <button
              style={{
                ...btnStyle,
                color: "#2563eb",
                borderColor: "#bfdbfe",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => setShowFilter((s) => !s)}
            >
              <Filter size={14} />
              Filters
            </button>
            {showFilter && (
              <div
                style={{
                  position: "absolute",
                  top: "110%",
                  right: 0,
                  zIndex: 20,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  padding: 14,
                  width: 220,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    marginBottom: 8,
                    color: "#334155",
                  }}
                >
                  Min. progress: {minProgress}%
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={10}
                  value={minProgress}
                  onChange={(e) => setMinProgress(Number(e.target.value))}
                  style={{ width: "100%" }}
                />
              </div>
            )}
          </div>

          <button
            style={{
              ...btnStyle,
              color: "#2563eb",
              borderColor: "#bfdbfe",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onClick={exportCSV}
          >
            <Download size={14} />
            Export
          </button>

          <button
            style={{
              ...btnStyle,
              background: "#2563eb",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onClick={addTask}
          >
            <Plus size={14} />
            Add Task
          </button>
        </div>
      </div>

      {/* Period navigator — this is what makes Year/Month act as real pickers */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
          padding: "10px 20px",
          borderBottom: "1px solid #eef0f3",
          background: "#fafbfc",
        }}
      >
        <button
          style={navBtnStyle}
          onClick={goPrev}
          title={`Previous ${viewType.toLowerCase()}`}
        >
          <ChevronLeft size={16} color="#475569" />
        </button>

        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#1e293b",
            minWidth: 140,
            textAlign: "center",
          }}
        >
          {label}
        </div>

        <button
          style={navBtnStyle}
          onClick={goNext}
          title={`Next ${viewType.toLowerCase()}`}
        >
          <ChevronRight size={16} color="#475569" />
        </button>

        {viewType === "Year" ? (
          <select
            value={periodStart.getFullYear()}
            onChange={(e) => setAnchor(new Date(Number(e.target.value), 0, 1))}
            style={{ ...selectStyle, marginLeft: 8 }}
          >
            {Array.from({ length: 9 }, (_, i) => 2022 + i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="month"
            value={`${periodStart.getFullYear()}-${String(periodStart.getMonth() + 1).padStart(2, "0")}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split("-").map(Number);
              setAnchor(new Date(y, m - 1, 1));
            }}
            style={{ ...selectStyle, marginLeft: 8 }}
          />
        )}

        <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 8 }}>
          {filtered.length} task{filtered.length !== 1 ? "s" : ""} in this{" "}
          {viewType.toLowerCase()}
        </span>
      </div>

      {/* Body */}
      {filtered.length === 0 ? (
        <div
          style={{
            padding: "48px 20px",
            textAlign: "center",
            color: "#94a3b8",
            fontSize: 13.5,
          }}
        >
          No tasks fall within {label}. Use the arrows above to jump to a
          different {viewType.toLowerCase()}, or{" "}
          <button
            onClick={addTask}
            style={{
              color: "#2563eb",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 13.5,
              textDecoration: "underline",
              padding: 0,
            }}
          >
            add one here
          </button>
          .
        </div>
      ) : (
        <div style={{ display: "flex" }}>
          {/* Left fixed table */}
          <div
            style={{ minWidth: TABLE_WIDTH, borderRight: "1px solid #eef0f3" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1.1fr 1.1fr 0.8fr 24px",
                padding: "10px 16px",
                fontSize: 12.5,
                fontWeight: 600,
                color: "#94a3b8",
                borderBottom: "1px solid #eef0f3",
                height: 40,
                alignItems: "center",
                background: "#fafbfc",
              }}
            >
              <div>Task Name</div>
              <div>Start Date</div>
              <div>End Date</div>
              <div>Progress</div>
              <div />
            </div>

            {filtered.map((t) => (
              <div
                key={t.id}
                ref={(el) => (rowRefs.current[t.id] = el)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.1fr 1.1fr 0.8fr 24px",
                  padding: "0 16px",
                  height: ROW_HEIGHT,
                  alignItems: "center",
                  borderBottom: "1px solid #f1f5f9",
                  fontSize: 13,
                  background: t.id === highlightId ? "#eff6ff" : "transparent",
                  transition: "background 0.6s ease",
                }}
              >
                {editingCell?.id === t.id && editingCell?.field === "name" ? (
                  <input
                    autoFocus
                    defaultValue={t.name}
                    onBlur={(e) => {
                      updateTask(t.id, { name: e.target.value || t.name });
                      setEditingCell(null);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    style={inputStyle}
                  />
                ) : (
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#1e293b",
                      cursor: "text",
                    }}
                    onDoubleClick={() =>
                      setEditingCell({ id: t.id, field: "name" })
                    }
                    title="Double-click to rename"
                  >
                    {t.id}. {t.name}
                  </div>
                )}

                <input
                  type="date"
                  value={toInputDate(t.start)}
                  onChange={(e) => {
                    const newStart = fromInputDate(e.target.value);
                    if (newStart < t.end) updateTask(t.id, { start: newStart });
                  }}
                  style={dateInputStyle}
                />

                <input
                  type="date"
                  value={toInputDate(t.end)}
                  onChange={(e) => {
                    const newEnd = fromInputDate(e.target.value);
                    if (newEnd > t.start) updateTask(t.id, { end: newEnd });
                  }}
                  style={dateInputStyle}
                />

                <input
                  type="number"
                  min={0}
                  max={100}
                  value={t.progress}
                  onChange={(e) =>
                    updateTask(t.id, {
                      progress: Math.max(
                        0,
                        Math.min(100, Number(e.target.value)),
                      ),
                    })
                  }
                  style={{ ...inputStyle, width: 52 }}
                />

                <button
                  onClick={() => deleteTask(t.id)}
                  title="Delete task"
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#cbd5e1",
                    display: "flex",
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Right timeline, scoped to the selected period only */}
          <div ref={scrollRef} style={{ overflowX: "auto", flex: 1 }}>
            <div style={{ position: "relative", width: totalWidth }}>
              {/* Column header */}
              <div
                style={{
                  position: "relative",
                  height: 40,
                  borderBottom: "1px solid #eef0f3",
                  background: "#fafbfc",
                }}
              >
                {columns.map((c) => (
                  <div
                    key={c.key}
                    style={{
                      position: "absolute",
                      left: c.left,
                      width: c.width,
                      height: "100%",
                      display: "flex",
                      flexDirection: viewType === "Month" ? "column" : "row",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: viewType === "Month" ? 11.5 : 12.5,
                      fontWeight: 600,
                      color: c.weekend ? "#cbd5e1" : "#94a3b8",
                      borderRight: "1px solid #f1f5f9",
                      boxSizing: "border-box",
                      lineHeight: 1.2,
                    }}
                  >
                    <span>{c.label}</span>
                    {c.sub && <span style={{ fontSize: 9.5 }}>{c.sub}</span>}
                  </div>
                ))}
              </div>

              {/* Grid + bars */}
              <div style={{ position: "relative", height: totalHeight }}>
                {columns.map((c) => (
                  <div
                    key={c.key}
                    style={{
                      position: "absolute",
                      left: c.left,
                      top: 0,
                      width: 0,
                      height: totalHeight,
                      borderRight: "1px solid #f1f5f9",
                    }}
                  />
                ))}

                {todayInPeriod && (
                  <div
                    style={{
                      position: "absolute",
                      left: todayLeft,
                      top: 0,
                      width: 0,
                      height: totalHeight,
                      borderLeft: "1.5px dashed #ef4444",
                      zIndex: 5,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: -18,
                        left: 6,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: "#ef4444",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Today
                    </span>
                  </div>
                )}

                {filtered.map((t, rowIndex) => {
                  const clampedStart =
                    t.start < periodStart ? periodStart : t.start;
                  const clampedEnd = t.end > periodEnd ? periodEnd : t.end;
                  const left = diffDays(clampedStart, periodStart) * pxPerDay;
                  const width = Math.max(
                    diffDays(clampedEnd, clampedStart) * pxPerDay,
                    16,
                  );
                  return (
                    <div
                      key={t.id}
                      style={{
                        position: "absolute",
                        left: 0,
                        top: rowIndex * ROW_HEIGHT,
                        width: "100%",
                        height: ROW_HEIGHT,
                        borderBottom: "1px solid #f1f5f9",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        onMouseDown={(e) => onBarMouseDown(e, t, "move")}
                        style={{
                          position: "absolute",
                          left,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width,
                          height: 20,
                          borderRadius: 6,
                          background: t.color,
                          cursor: "grab",
                          boxShadow:
                            t.id === highlightId
                              ? "0 0 0 3px #93c5fd, 0 1px 2px rgba(0,0,0,0.12)"
                              : "0 1px 2px rgba(0,0,0,0.12)",
                          overflow: "hidden",
                          transition: "box-shadow 0.6s ease",
                        }}
                        title={`${t.name}: ${fmt(t.start)} → ${fmt(t.end)} (${t.progress}%)`}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: `${t.progress}%`,
                            background: "rgba(255,255,255,0.35)",
                          }}
                        />
                        <div
                          onMouseDown={(e) =>
                            onBarMouseDown(e, t, "resize-left")
                          }
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            cursor: "ew-resize",
                          }}
                        />
                        <div
                          onMouseDown={(e) =>
                            onBarMouseDown(e, t, "resize-right")
                          }
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            cursor: "ew-resize",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          padding: "8px 20px",
          fontSize: 11.5,
          color: "#94a3b8",
          borderTop: "1px solid #f1f5f9",
        }}
      >
        Only tasks touching {label} are shown. Switch View to Year for a
        12-month overview, or use the arrows/picker to jump between periods.
      </div>
    </div>
  );
}

const btnStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "7px 12px",
  fontSize: 13,
  color: "#334155",
  background: "#fff",
  cursor: "pointer",
};
const iconBtnStyle = {
  ...btnStyle,
  width: 34,
  height: 34,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const selectStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "7px 10px",
  fontSize: 13,
  color: "#334155",
  background: "#fff",
  cursor: "pointer",
};
const navBtnStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  width: 30,
  height: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fff",
  cursor: "pointer",
};
const inputStyle = {
  border: "1px solid transparent",
  background: "transparent",
  fontSize: 13,
  color: "#64748b",
  width: "90%",
  padding: "2px 4px",
  borderRadius: 4,
};
const dateInputStyle = { ...inputStyle, fontSize: 12, cursor: "pointer" };
